import { useEffect, useState } from 'react';
import { getDataset, getDatasets, getMetadata } from '@/api/ecoinvent';

const itemsPerPage = 20;

const formatEcoinventQuery = (ecoinventQuery, page = 1, searchTerm = '', filters = {}) => {
  const params = new URLSearchParams();

  if (ecoinventQuery) {
    Object.entries(ecoinventQuery).forEach(([key, value]) => {
      params.append(key, value);
    });
  }

  if (searchTerm) {
    params.append('q', searchTerm);
  }

  Object.entries(filters).forEach(([key, values]) => {
    if (values.length > 0) {
      values.forEach(v => params.append(key, v));
    }
  });

  params.append('page', page.toString());

  return params.toString();
};

const useEcoinventData = (ecoinventQuery, datasetId) => {
  const [selectedItem, setSelectedItem] = useState(null);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [metadata, setMetadata] = useState(null);
  const [geographyFilter, setGeographyFilter] = useState([]);
  const [activityNameFilter, setActivityNameFilter] = useState([]);
  const [referenceProductFilter, setReferenceProductFilter] = useState([]);

  const queryFilters = {
    ecoinvent_geography: geographyFilter,
    ecoinvent_activity_name: activityNameFilter,
    ecoinvent_reference_product: referenceProductFilter,
  };

  const datasetQueryStr = formatEcoinventQuery(ecoinventQuery);
  const datasetsQueryStr = formatEcoinventQuery(ecoinventQuery, currentPage, searchTerm, queryFilters);

  useEffect(() => {
    if (!datasetId || !datasetQueryStr) return;

    const fetchDataset = async () => {
      const data = await getDataset(datasetQueryStr, datasetId);
      if (data?.items?.length) {
        setSelectedItem(data?.items[0]);
      }
    };

    fetchDataset();
  }, [datasetId, datasetQueryStr]);

  useEffect(() => {
    if (!datasetsQueryStr) return;

    const fetchDatasets = async () => {
      setLoading(true);
      setError(null);

      try {
        const data = await getDatasets(datasetsQueryStr, currentPage);

        if (data?.items) {
          const { items, count } = data;
          setData(items);
          setTotalCount(count);
        }
      } catch (err) {
        setError(err.message || 'Failed to fetch ecoinvent data.');
        setData([]);
        setTotalCount(0);
      } finally {
        setLoading(false);
      }
    };

    fetchDatasets();
  }, [datasetsQueryStr, currentPage]);

  useEffect(() => {
    const fetchMetadata = async () => {
      try {
        const data = await getMetadata();
        setMetadata(data);
      } catch (err) {
        console.error('Failed to fetch ecoinvent metadata:', err);
      }
    };

    fetchMetadata();
  }, []);

  const totalPages = Math.ceil(totalCount / itemsPerPage);

  const handlePageChange = newPage => {
    setCurrentPage(newPage);
  };

  const handleSearch = term => {
    setSearchTerm(term);
    setCurrentPage(1);
  };

  const filterSetters = {
    geography: setGeographyFilter,
    activityName: setActivityNameFilter,
    referenceProduct: setReferenceProductFilter,
  };

  const handleFilterChange = (key, values) => {
    filterSetters[key](values);
    setCurrentPage(1);
  };

  return {
    data,
    loading,
    error,
    metadata,
    selectedItem,
    setSelectedItem,
    currentPage,
    totalPages,
    totalCount,
    handlePageChange,
    searchTerm,
    handleSearch,
    filters: {
      geography: geographyFilter,
      activityName: activityNameFilter,
      referenceProduct: referenceProductFilter,
    },
    onFilterChange: handleFilterChange,
  };
};

export default useEcoinventData;
